import React, { useCallback, useContext, useMemo, useState } from 'react';

export type EditorUploadContextType = {
  isUploading: boolean;
  setEditorUploading: (editorId: string, isUploading: boolean) => void;
};

export const EditorUploadContext = React.createContext<EditorUploadContextType | null>(null);

// Tracks rich text editors with uploads in progress so forms can block saving until they finish.
export const EditorUploadProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const [uploadingEditorIds, setUploadingEditorIds] = useState<Set<string>>(new Set());

  const setEditorUploading = useCallback((editorId: string, isUploading: boolean) => {
    setUploadingEditorIds((prev) => {
      if (prev.has(editorId) === isUploading) {
        return prev;
      }
      const next = new Set(prev);
      if (isUploading) {
        next.add(editorId);
      } else {
        next.delete(editorId);
      }
      return next;
    });
  }, []);

  const providerValue = useMemo(
    () => ({ isUploading: uploadingEditorIds.size > 0, setEditorUploading }),
    [uploadingEditorIds, setEditorUploading],
  );

  return <EditorUploadContext.Provider value={providerValue}>{children}</EditorUploadContext.Provider>;
};

export const useEditorUpload = () => useContext(EditorUploadContext);
